import { roll } from "./runtime";
/* Slide Studio — dependency-free navigation, fragments, presenter, and teaching tools. */
(() => {
  "use strict";
  const slides = [...document.querySelectorAll(".ss-slide")];
  const deck = document.querySelector(".ss-deck");
  const board = document.querySelector(".ss-live-board");
  const help = document.querySelector(".ss-help");
  const pointer = document.querySelector(".ss-pointer");
  const palette = document.querySelector(".ss-highlight-palette");
  const motion = document.body.dataset.motion || "cinematic";
  const highlightColors = ["#66551f", "#28543a", "#244d66", "#61384f", "#6a4227"].map((fallback, index) => getComputedStyle(document.body).getPropertyValue(`--highlight-${index + 1}`).trim() || fallback);
  const deckId = document.body.dataset.deckId || location.pathname;
  const channel = "BroadcastChannel" in window && !document.body.classList.contains("org-studio") ? new BroadcastChannel(`slide-studio:${deckId}`) : null;
  let index = Math.max(0, Math.min(slides.length - 1, Number(location.hash.slice(1)) || 0));
  let fragmentIndex = -1;
  let presenterWindow = null;
  let notesWindow = null;
  let pendingHighlightRange = null;
  let overview = null;
  let synchronized = false;

  function ensureTableCards(root) {
    root.querySelectorAll(".ss-body table").forEach(table => {
      if (table.parentElement?.classList.contains("ss-table-card")) return;
      const card = document.createElement("div");
      card.className = "ss-table-card";
      table.before(card);
      card.appendChild(table);
    });
  }

  slides.forEach(ensureTableCards);

  function fitTitles() {
    if (document.body.dataset.theme !== "minimal") return;
    slides.filter(slide => slide.classList.contains("layout-title")).forEach(slide => {
      const content = slide.querySelector(".ss-content"), title = content?.querySelector("h1");
      if (!title || !content.clientWidth || !content.clientHeight) return;
      let low = 1, high = content.clientWidth * .30;
      // Include subtitle/kicker, margins and wrapped words in the measured fit.
      for (let i = 0; i < 14; i++) {
        const size = (low + high) / 2;
        title.style.fontSize = `${size}px`;
        if (content.scrollHeight <= content.clientHeight + 1 && title.scrollWidth <= title.clientWidth + 1) low = size;
        else high = size;
      }
      title.style.fontSize = `${Math.floor(low * 10) / 10}px`;
    });
  }
  let fitFrame;
  function scheduleTitleFit() { cancelAnimationFrame(fitFrame); fitFrame = requestAnimationFrame(fitTitles); }
  new ResizeObserver(scheduleTitleFit).observe(deck);
  slides.forEach(slide => new MutationObserver(scheduleTitleFit).observe(slide.querySelector(".ss-content"), {subtree:true, childList:true, characterData:true}));
  document.fonts?.ready.then(scheduleTitleFit);
  document.fonts?.addEventListener("loadingdone", scheduleTitleFit);
  addEventListener("resize", scheduleTitleFit);


  slides.forEach(slide => {
    if (!slide.classList.contains("layout-questions")) return;
    slide.querySelectorAll(".ss-body ol > li").forEach((item, position) => {
      if (item.querySelector(":scope > .ss-question-response")) return;
      const response = document.createElement("div");
      response.className = "ss-question-response";
      response.contentEditable = "true";
      response.setAttribute("role", "textbox");
      response.setAttribute("aria-label", `Response to question ${position + 1}`);
      response.spellcheck = true;
      item.appendChild(response);
    });
  });

  const current = () => slides[index];
  const fragments = () => [...current().querySelectorAll(".fragment,[data-reveal]")];
  const state = () => ({ ...roll.state, stepCount:roll.deck.slides[index]?.steps.length ?? 0, type:"state", blackout:document.body.classList.contains("ss-blackout"), index, fragmentIndex, board:board.textContent, boardVisible:board.classList.contains("is-visible"), motion });
  const broadcast = message => {
    channel?.postMessage(message);
    if (presenterWindow && !presenterWindow.closed) presenterWindow.postMessage(message, "*");
    if (notesWindow && !notesWindow.closed) notesWindow.postMessage(message, "*");
    if (window.opener && !window.opener.closed) window.opener.postMessage(message, "*");
    if (window.parent !== window) window.parent.postMessage(message, "*");
  };

  function render(announce = true) {
    const previousIndex = index;
    index = roll.state.slideIndex; fragmentIndex = roll.state.stepIndex - 1;
    slides.forEach((slide, position) => {
      const entering = position === index && !slide.classList.contains("is-active");
      slide.classList.toggle("is-active", position === index);
      slide.classList.toggle("is-current", position === index);
      if (position !== index) slide.classList.remove("is-entering");
      if (entering) requestAnimationFrame(() => slide.classList.add("is-entering"));
    });
    if (previousIndex !== index && window.parent === window) current().focus({preventScroll:true});
    deck.style.setProperty("--progress", `${((index + 1) / slides.length) * 100}%`);
    roll.refresh();
    if (announce) broadcast(state());
    presenterRender();
  }

  function next() { roll.dispatch("next"); render(); }
  function previous() { roll.dispatch("previous"); render(); }
  function go(position) { roll.dispatch({slideIndex:position}); render(); }
  function force(command) { roll.dispatch(command); render(); }
  function toggleFullscreen() { document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen(); }

  function toggleBlackout() {
    document.body.classList.toggle("ss-blackout");
    broadcast({ type:"blackout", visible:document.body.classList.contains("ss-blackout") });
  }

  function toggleOverview() {
    if (overview) { overview.remove(); overview = null; current().focus(); return; }
    overview = document.createElement("div");
    overview.className = "ss-overview";
    overview.setAttribute("role", "dialog");
    overview.setAttribute("aria-label", "Slide overview");
    slides.forEach((slide, position) => {
      const button = document.createElement("button");
      button.className = position === index ? "is-current" : "";
      button.textContent = `${position + 1} · ${slide.querySelector("h1")?.textContent || "Untitled"}`;
      button.addEventListener("click", () => { go(position); toggleOverview(); });
      overview.appendChild(button);
    });
    document.body.appendChild(overview);
    overview.querySelector("button")?.focus();
  }

  function syncCurrentHTML() {
    broadcast({ type:"slide-html", index, html:current().querySelector(".ss-content").innerHTML });
    presenterRender();
  }

  function highlightSelection() {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.rangeCount) return;
    pendingHighlightRange = selection.getRangeAt(0).cloneRange();
    const rect = pendingHighlightRange.getBoundingClientRect();
    palette.style.left = `${Math.max(12, Math.min(innerWidth - 260, rect.left + rect.width / 2 - 120))}px`;
    palette.style.top = `${Math.max(12, rect.top - 68)}px`;
    palette.classList.add("is-visible");
  }

  function applyHighlight(color) {
    if (!pendingHighlightRange) return;
    const mark = document.createElement("mark");
    mark.style.background = color;
    try { pendingHighlightRange.surroundContents(mark); }
    catch { mark.appendChild(pendingHighlightRange.extractContents()); pendingHighlightRange.insertNode(mark); }
    window.getSelection()?.removeAllRanges();
    pendingHighlightRange = null;
    palette.classList.remove("is-visible");
    syncCurrentHTML();
  }

  highlightColors.forEach(color => {
    const button = document.createElement("button");
    button.className = "ss-highlight-color"; button.style.background = color;
    button.setAttribute("aria-label", `Highlight ${color}`);
    button.addEventListener("click", () => applyHighlight(color));
    palette.appendChild(button);
  });
  deck.addEventListener("click", event => {
    const mark = event.target.closest("mark");
    if (!mark || !current().contains(mark)) return;
    mark.replaceWith(...mark.childNodes); syncCurrentHTML();
  });
  deck.addEventListener("input", event => {
    if (event.target.closest(".ss-question-response") && current().contains(event.target))
      syncCurrentHTML();
  });
  deck.addEventListener("click", event => {
    const answer = event.target.closest(".layout-answer");
    if (answer) answer.classList.toggle("is-answer-revealed");
  });

  function addElectricParticles() {
    if (document.body.dataset.theme !== "electric-revival") return;
    for (let i = 0; i < 28; i += 1) {
      const particle = document.createElement("i");
      particle.className = "ss-electric-particle";
      particle.style.setProperty("--particle-x", `${(i * 37) % 100}%`);
      particle.style.setProperty("--particle-y", `${(i * 61) % 100}%`);
      particle.style.setProperty("--particle-delay", `${(i % 9) * -0.7}s`);
      deck.appendChild(particle);
    }
  }
  addElectricParticles();

  function presenterMarkup() {
    const stylesheet = document.querySelector('link[rel="stylesheet"]')?.href || "";
    const themeStylesheet = [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.href.includes("slide-studio-themes/"))?.href || "";
    const notesUrl = document.body.dataset.notesUrl || "";
    const speakerNotesUrl = notesUrl ? `${notesUrl}${notesUrl.includes("?") ? "&" : "?"}speaker=1` : "";
    const notesAttribute = speakerNotesUrl.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    const theme = document.body.dataset.theme || "midnight";
    return `<!doctype html><html><head><meta charset="utf-8"><title>Speaker View</title><link rel="stylesheet" href="${stylesheet}"><style>
      *{box-sizing:border-box}html,body{overflow:hidden}body{margin:0;padding:6px;height:100vh;display:grid;grid-template-rows:42px minmax(0,1fr);gap:6px;background:#07101a;color:#edf2f4;font:14px system-ui,sans-serif}header{display:flex;align-items:center;gap:6px;min-width:0;overflow:hidden}button{height:34px;padding:6px 10px;border:0;border-radius:7px;background:#bca36a;color:#101820;font-weight:700;white-space:nowrap}button.secondary{background:#26384a;color:#edf2f4}.timer{margin-left:auto;font:700 20px ui-monospace,monospace}.status{color:#9baab8;white-space:nowrap}.notes-panel{min-width:0;min-height:0;overflow:hidden}.notes-frame{display:block;width:100%;height:100%;border:1px solid #33485d;border-radius:8px;background:#07101a}.notes-empty{height:100%;display:grid;place-items:center;padding:2rem;color:#9baab8;border:1px solid #33485d;border-radius:8px;text-align:center}@media(max-width:760px){body{grid-template-rows:78px minmax(0,1fr)}header{align-content:center;flex-wrap:wrap}.timer{margin-left:0}}</style>${themeStylesheet ? `<link rel="stylesheet" href="${themeStylesheet}">` : ""}</head><body data-theme="${theme}">
      <header><button id="prev">← Previous</button><button id="next">Next →</button><button class="secondary" id="notesPrev">K · Notes ↑</button><button class="secondary" id="notesNext">J · Notes ↓</button><button class="secondary" id="highlight">Highlight</button><button class="secondary" id="clearHighlights">Clear highlights</button><span class="status" id="status"></span><span class="timer" id="timer" role="button" tabindex="0" title="Click to reset timer">00:00</span></header>
      <main class="notes-panel">${notesAttribute ? `<iframe class="notes-frame" id="notesFrame" title="Speaking notes" src="${notesAttribute}"></iframe>` : '<div class="notes-empty">Add #+SLIDE_NOTES_FILE to the deck and export the project to show notes here.</div>'}</main>
      <script>const openerWindow=window.opener;let start=Date.now();const status=document.querySelector('#status'),notesFrame=document.querySelector('#notesFrame'),timer=document.querySelector('#timer');
      function send(message){openerWindow.postMessage(message,'*')}
      function command(name){send({type:'command',command:name})}
      function noteCommand(delta){notesFrame?.contentWindow.postMessage({type:'notes-step',delta},'*')}
      prev.onclick=()=>command('previous');next.onclick=()=>command('next');notesPrev.onclick=()=>noteCommand(-1);notesNext.onclick=()=>noteCommand(1);
      highlight.onclick=()=>notesFrame?.contentWindow.postMessage({type:'notes-highlight'},'*');clearHighlights.onclick=()=>notesFrame?.contentWindow.postMessage({type:'notes-clear-highlights'},'*');
      timer.addEventListener('click',()=>{start=Date.now();timer.textContent='00:00'});timer.addEventListener('keydown',event=>{if(event.key==='Enter'||event.key===' '){event.preventDefault();timer.click()}});
      addEventListener('keydown',event=>{const editing=event.target.closest&&event.target.closest('[contenteditable=true],textarea,input');if(editing)return;if(event.key.toLowerCase()==='b'&&!event.repeat&&!event.ctrlKey&&!event.metaKey&&!event.altKey){event.preventDefault();command('blackout')}else if(['ArrowRight','ArrowDown','PageDown',' '].includes(event.key)){event.preventDefault();command(event.shiftKey?'next-slide':'next')}else if(['ArrowLeft','ArrowUp','PageUp'].includes(event.key)){event.preventDefault();command(event.shiftKey?'previous-slide':'previous')}else if(['j','J'].includes(event.key)){event.preventDefault();noteCommand(1)}else if(['k','K'].includes(event.key)){event.preventDefault();noteCommand(-1)}});
      addEventListener('message',event=>{const m=event.data;if(m.type==='presenter-render'){status.textContent=(m.index+1)+' / '+m.total+' · Step '+m.stepIndex+' / '+m.stepCount;notesFrame?.contentWindow.postMessage({type:'state',index:m.index,stepIndex:m.stepIndex,stepCount:m.stepCount},'*')}else if(m.type==='command'||m.type==='go'||m.type==='notes-slide-html'||m.type==='notes-ready')send(m)});
      setInterval(()=>{const seconds=Math.floor((Date.now()-start)/1000);timer.textContent=String(Math.floor(seconds/60)).padStart(2,'0')+':'+String(seconds%60).padStart(2,'0')},1000);send({type:'presenter-ready'});<\/script></body></html>`;
  }

  function presenterRender() {
    if (!presenterWindow || presenterWindow.closed) return;
    const cloneSlide = slide => {
      if (!slide) return null;
      const clone = slide.cloneNode(true);
      clone.classList.add("is-active"); clone.style.position = "relative";
      return clone.outerHTML;
    };
    const hiddenFragment = [...current().querySelectorAll(".fragment")].findIndex(fragment => !fragment.classList.contains("is-visible"));
    let upcoming = slides[index + 1], nextLabel = "Next slide";
    if (hiddenFragment >= 0) {
      upcoming = current().cloneNode(true);
      [...upcoming.querySelectorAll(".fragment")].forEach((fragment, position) =>
        fragment.classList.toggle("is-visible", position <= hiddenFragment));
      nextLabel = "Next fragment";
    }
    presenterWindow.postMessage({ type:"presenter-render", index, total:slides.length, stepIndex:roll.state.stepIndex, stepCount:roll.deck.slides[index].steps.length,
      currentHtml:cloneSlide(current()), nextHtml:cloneSlide(upcoming), nextLabel,
      editable:current().dataset.editable === "true", board:board.textContent }, "*");
  }
  function openPresenter() {
    presenterWindow = window.open("", `slide-studio-presenter-${deckId}`, "popup,width=1500,height=900");
    if (!presenterWindow) return alert("Allow popups to open speaker view.");
    presenterWindow.document.open(); presenterWindow.document.write(presenterMarkup()); presenterWindow.document.close();
  }
  function openNotes() {
    const notesUrl = document.body.dataset.notesUrl;
    if (!notesUrl) return alert("Add #+SLIDE_NOTES_FILE to this deck and export its notes page.");
    notesWindow = window.open(notesUrl, `slide-studio-notes-${deckId}`, "popup,width=900,height=1100");
    if (!notesWindow) return alert("Allow popups to open speaker notes.");
    setTimeout(() => notesWindow?.postMessage(state(), "*"), 250);
  }

  function receive(message) {
    if (!message || typeof message !== "object") return;
    if (message.type === "state" && Number.isInteger(message.index)) {
      synchronized = true;
      if (typeof message.blackout === "boolean") document.body.classList.toggle("ss-blackout", message.blackout);
      roll.dispatch({slideIndex:message.index, stepIndex:message.stepIndex ?? (message.fragmentIndex ?? -1) + 1});
      render(false);
    } else if (message.type === "audience-ready") {
      broadcast(state());
    } else if (message.type === "command") {
      if (message.command === "next") next();
      if (["next-slide","previous-slide","home","end","reset"].includes(message.command)) force(message.command);
      if (message.command === "previous") previous();
      if (message.command === "blackout") toggleBlackout();
      if (message.command === "toggle-board") { board.classList.toggle("is-visible"); broadcast(state()); }
      if (message.command === "open-notes") openNotes();
    } else if (message.type === "board") {
      board.textContent = message.text || ""; board.classList.toggle("is-visible", Boolean(message.visible && message.text)); broadcast(state());
    } else if (message.type === "go") {
      go(Number(message.index));
    } else if (message.type === "notes-slide-html" && slides[message.index]) {
      slides[message.index].querySelector(".ss-content").innerHTML = message.html;
      roll.rehydrate(message.index);
      broadcast({ type:"slide-html", index:message.index, html:message.html });
      if (message.index === index) presenterRender();
    } else if (message.type === "presenter-html" && message.index === index) {
      current().querySelector(".ss-content").innerHTML = message.html; roll.rehydrate(index); broadcast({ type:"slide-html", index, html:message.html });
    } else if (message.type === "slide-html" && slides[message.index]) {
      slides[message.index].querySelector(".ss-content").innerHTML = message.html;
      roll.rehydrate(message.index);
      if (message.index === index) presenterRender();
    } else if (message.type === "presenter-ready") presenterRender();
    else if (message.type === "blackout") document.body.classList.toggle("ss-blackout", Boolean(message.visible));
    else if (message.type === "notes-ready") { broadcast(state()); presenterRender(); }
  }
  channel && (channel.onmessage = event => receive(event.data));
  addEventListener("message", event => receive(event.data));
  addEventListener("keydown", event => {
    if (event.target.closest?.('[contenteditable="true"],textarea,input')) return;
    if (["ArrowRight","ArrowDown","PageDown"," ","j","J"].includes(event.key)) { event.preventDefault(); event.shiftKey ? force("next-slide") : next(); }
    else if (["ArrowLeft","ArrowUp","PageUp","k","K"].includes(event.key)) { event.preventDefault(); event.shiftKey ? force("previous-slide") : previous(); }
    else if (event.key === "Home") force("home"); else if (event.key === "End") force("end");
    else if (event.key.toLowerCase() === "f") toggleFullscreen();
    else if (event.key.toLowerCase() === "s") openPresenter();
    else if (event.key.toLowerCase() === "n") openNotes();
    else if (event.key.toLowerCase() === "b" && !event.repeat && !event.ctrlKey && !event.metaKey && !event.altKey) { event.preventDefault(); toggleBlackout(); }
    else if (event.key.toLowerCase() === "o") toggleOverview();
    else if (event.key.toLowerCase() === "r") { force("reset"); }
    else if (event.key.toLowerCase() === "h") highlightSelection();
    else if (event.key === "?") { help.classList.toggle("is-visible"); if(help.classList.contains("is-visible")) help.focus(); else current().focus(); }
    else if (event.key === "Escape") { help.classList.remove("is-visible"); if (overview) toggleOverview(); current().focus(); }
  });
  deck.addEventListener("pointermove", event => { if (!event.altKey) return; const rect=deck.getBoundingClientRect(); pointer.style.left=`${event.clientX-rect.left}px`;pointer.style.top=`${event.clientY-rect.top}px`;pointer.classList.add("is-visible");broadcast({type:"pointer",x:(event.clientX-rect.left)/rect.width,y:(event.clientY-rect.top)/rect.height,visible:true}) });
  deck.addEventListener("pointerleave",()=>pointer.classList.remove("is-visible"));
  help.innerHTML = '<h2>Slide Studio · progressive roll</h2><p>Arrows / Page Up–Down / Space: next or previous step. Shift+arrows: force slide. Home / End: deck beginning / end. R: reset steps. F: fullscreen. S: speaker view. N: notes. O: overview. B: blackout. Select text then H: highlight. Hold Alt: pointer. ?: help. Escape: close overlays.</p>';
  help.tabIndex = -1;
  addEventListener("hashchange", () => {
    const [slideIndex, stepIndex] = location.hash.slice(1).split("/").map(Number);
    roll.dispatch({slideIndex,stepIndex}); render();
  });
  render(false);
  broadcast({type:"audience-ready"});
  setTimeout(() => { if (!synchronized) broadcast(state()); }, 100);
})();
